import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EcommerceNav from '../../components/ecommerce/EcommerceNav';
import EcommerceFooter from '../../components/ecommerce/EcommerceFooter';
import './Ecommerce.css';
import './Cart.css';
import { formatINR } from '../../utils/currency';

const Cart = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (authLoading) return; // Wait for auth state to resolve

    if (user) {
      fetchCart();
    } else {
      // Not authenticated; stay on cart page and show empty state/CTA
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/cart');
      
      if (response.data.success) {
        setCart(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      setUpdating(true);
      await api.put('/api/ecommerce/cart/update', {
        itemId,
        quantity
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart item:', error);
      alert('Error updating cart item');
    } finally {
      setUpdating(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setUpdating(true);
      await api.delete(`/api/ecommerce/cart/remove/${itemId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      const message = error.response?.data?.message || 'Error removing item from cart';
      alert(message);
    } finally {
      setUpdating(false);
    }
  };

  const formatPrice = (price) => formatINR(price);

  const handleCheckout = () => {
    navigate('/ecommerce/checkout');
  };

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading">Loading cart...</div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="cart-container">
        <div className="empty-cart">
          <h2>Your cart is empty</h2>
          <p>Add some products to get started!</p>
          <Link to="/ecommerce" className="continue-shopping-btn">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ecommerce-page">
      <EcommerceNav />
      <div className="cart-container">
        <div className="cart-header">
        <h1>Shopping Cart</h1>
        <p>{cart.totalItems} item(s) in your cart</p>
      </div>

      <div className="cart-content">
        <div className="cart-items">
          {cart.items.map((item) => (
            <div key={item._id} className="cart-item">
              <div className="item-image">
                {item.product.inventory.images && item.product.inventory.images.length > 0 ? (
                  <img
                    src={item.product.inventory.images[0]}
                    alt={item.product.inventory.itemName}
                    onError={(e) => {
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                ) : (
                  <div className="placeholder-image">
                    <span>No Image</span>
                  </div>
                )}
              </div>

              <div className="item-details">
                <h3 className="item-name">
                  <Link to={`/ecommerce/products/${item.product.slug}`}>
                    {item.product.inventory.itemName}
                  </Link>
                </h3>
                <p className="item-code">SKU: {item.product.inventory.itemCode}</p>
                
                {item.variant && (
                  <p className="item-variant">
                    {item.variant.name}: {item.variant.value}
                  </p>
                )}

                <div className="item-price">
                  {formatPrice(item.price)} each
                </div>
              </div>

              <div className="item-quantity">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => updateCartItem(item._id, item.quantity - 1)}
                    disabled={updating || item.quantity <= 1}
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() => updateCartItem(item._id, item.quantity + 1)}
                    disabled={updating}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="item-total">
                <div className="total-price">
                  {formatPrice(item.price * item.quantity)}
                </div>
                <button
                  className="remove-btn"
                  onClick={() => removeFromCart(item._id)}
                  disabled={updating}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="cart-summary">
          <div className="summary-card">
            <h3>Order Summary</h3>
            
            <div className="summary-row">
              <span>Subtotal ({cart.totalItems} items)</span>
              <span>{formatPrice(cart.totalAmount)}</span>
            </div>
            
            <div className="summary-row">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            
            <div className="summary-row">
              <span>Tax</span>
              <span>Calculated at checkout</span>
            </div>
            
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatPrice(cart.totalAmount)}</span>
            </div>

            <button
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={updating}
            >
              Proceed to Checkout
            </button>

            <Link to="/ecommerce" className="continue-shopping-link">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
      </div>
      <EcommerceFooter />
    </div>
  );
};

export default Cart;
